import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { HsnCode, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * One code's description and rates, saved on its own.
 *
 * A row at a time rather than one Save for the table: a rate is a tax fact an
 * invoice will be issued at (FR-GST-7), so each change is one deliberate
 * action with its own result, never a batch where one bad entry blocks the
 * rest or a stray keystroke rides along with an intended edit.
 *
 * Keyed on `updatedAt` by the caller, so a saved row starts again from what
 * the server returned instead of keeping a stale draft.
 */
function RateRow({ hsn }: { hsn: HsnCode }) {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const [description, setDescription] = useState(hsn.description);
  const [gstRate, setGstRate] = useState(hsn.gstRate);
  const [cessRate, setCessRate] = useState(hsn.cessRate);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const dirty =
    description !== hsn.description ||
    gstRate !== hsn.gstRate ||
    cessRate !== hsn.cessRate;

  const save = useMutation({
    mutationFn: () =>
      data.catalog.updateHsnCode(hsn.id, { description, gstRate, cessRate }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        void queryClient.invalidateQueries({ queryKey: ["catalog", "hsn"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const fieldError = (name: string) =>
    rejection?.kind === "invalid" ? rejection.fields?.[name] : undefined;

  return (
    <tr className="border-b align-top last:border-b-0">
      <td className="px-3 py-2 font-medium tabular-nums">{hsn.code}</td>
      <td className="px-3 py-2">
        <Input
          aria-label={`Description for ${hsn.code}`}
          value={description}
          aria-invalid={fieldError("description") !== undefined}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
      </td>
      {(
        [
          ["gstRate", gstRate, setGstRate, "GST rate"],
          ["cessRate", cessRate, setCessRate, "Cess rate"],
        ] as const
      ).map(([name, value, set, label]) => (
        <td key={name} className="px-3 py-2">
          <div className="flex items-center gap-1">
            <Input
              aria-label={`${label} for ${hsn.code}`}
              inputMode="decimal"
              value={value}
              aria-invalid={fieldError(name) !== undefined}
              onChange={(event) => {
                set(event.target.value);
              }}
              className="w-20 text-right tabular-nums"
            />
            <span className="text-muted-foreground text-sm">%</span>
          </div>
          {fieldError(name) ? (
            <p className="text-destructive mt-1 text-xs">{fieldError(name)}</p>
          ) : null}
        </td>
      ))}
      <td className="px-3 py-2 text-right tabular-nums">
        {hsn.productCount ?? "—"}
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        {rejection && rejection.kind !== "invalid" ? (
          <p className="text-destructive mt-1 text-xs">{rejection.message}</p>
        ) : null}
        {save.isError ? (
          <p className="text-destructive mt-1 text-xs">{save.error.message}</p>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * GST and cess rates per HSN code (FR-CAT-10, FR-GST-7).
 *
 * The codes come from Shopify with the variants; the rates are the ERP's, and
 * this is where they are changed. New codes arrive at 3% with no cess, the
 * imitation-jewellery rate — so a code with anything else to pay needs
 * correcting here before it is invoiced.
 */
export function HsnRates() {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["catalog", "hsn", "list", "gst-rates"],
    queryFn: () => data.catalog.listHsnCodes({ limit: 250 }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>HSN rates</CardTitle>
        <CardDescription>
          Codes come from Shopify with each product. The GST and cess rate an
          invoice charges comes from here. New codes start at 3% GST and 0%
          cess.
        </CardDescription>
      </CardHeader>
      <CardContent className="px-0">
        {query.isPending ? (
          <div className="px-6">
            <TableSkeleton rows={3} />
          </div>
        ) : query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : query.data.items.length === 0 ? (
          <p className="text-muted-foreground px-6 text-sm">
            No HSN codes yet. They appear after a catalogue sync brings in
            products that carry one.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr className="border-b">
                  {["Code", "Description", "GST", "Cess", "Products", ""].map(
                    (label, index) => (
                      <th
                        key={label || index}
                        className={cn(
                          "px-3 py-2 text-left font-medium",
                          index >= 4 && "text-right",
                        )}
                      >
                        {label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((hsn) => (
                  <RateRow key={`${hsn.id}:${hsn.updatedAt}`} hsn={hsn} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
